import React from 'react';
import styled from 'styled-components';

const PolicyContainer = styled.div`
  padding: 40px 20px;
  max-width: 800px;
  margin: 0 auto;
  font-family: 'Arial', sans-serif;
  color: #FFFFFF; /* Main text to white */

  h1, h2 {
    color: #FFFFFF; /* Headings to white */
    margin-bottom: 10px;
  }

  p, li {
    line-height: 1.6;
    margin-bottom: 10px;
  }

  ul {
    padding-left: 20px;
  }

  a {
    color: #ADD8E6; /* Light blue for links */
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }
`;

const PaymentLogosContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 15px; /* Space between logos */
  margin-top: 10px;
  flex-wrap: wrap; /* Allow logos to wrap on smaller screens */
`;

const PaymentLogo = styled.img`
  height: 30px; /* Adjust height as needed */
  width: auto;   /* Maintain aspect ratio */
`;

const FulfillmentPolicy: React.FC = () => {
  return (
    <PolicyContainer>
      <h1>Fulfillment Policy</h1>

      <h2>1. Description of Services</h2>
      <p>MarketNest provides comprehensive digital marketing services, including but not limited to: Search Engine Optimization (SEO), Pay-Per-Click (PPC) Advertising, Social Media Marketing, Content Marketing, Email Marketing, Web Design & Development, Analytics & Reporting, and Conversion Rate Optimization (CRO). Detailed descriptions of each service are available on their respective pages on our website.</p>
      <p>Our services are tailored to meet the specific needs of each client, with clear deliverables and project timelines outlined in individual service agreements or statements of work.</p>

      <h2>2. Purchase Currency</h2>
      <p>All prices for services are listed and charged in United States Dollars (USD).</p>

      <h2>3. Customer Service Contact Information</h2>
      <p>We are committed to providing excellent customer support. You can reach us through the following channels:</p>
      <ul>
        <li><strong>Email:</strong> <a href="mailto:support@marketnest.com">support@marketnest.com</a></li>
        <li><strong>Phone:</strong> <a href="tel:+1-919-504-1542">919-504-1542</a></li>
        <li><strong>Contact Form:</strong> Available on our <a href="/contact">Contact Us page</a>.</li>
      </ul>
      <p>Our support team is available Monday to Friday, 9:00 AM to 5:00 PM (Eastern Time).</p>

      <h2>4. Fulfillment Policies</h2>
      <p>Our fulfillment policies are designed to ensure clarity and satisfaction for our clients.</p>
      
      <h3>Service Delivery:</h3>
      <p>Upon agreement and initial payment (if applicable), MarketNest will commence service delivery as outlined in the client's specific service agreement. Project timelines and milestones will be communicated and tracked throughout the engagement. Access to deliverables, reports, and campaign performance will be provided through our client portal or direct communication.</p>

      <h3>Refund Policy:</h3>
      <p>Due to the nature of digital marketing services and the resources allocated upfront, MarketNest generally does not offer refunds for services already rendered or in progress. However, client satisfaction is important to us. If you are unsatisfied with our services, please contact us to discuss your concerns. Specific refund conditions, if any, will be detailed in your individual service agreement.</p>
      <p>For subscription-based services, refunds for unused portions of a subscription period may be considered on a case-by-case basis, as outlined in the service agreement.</p>

      <h3>Cancellation Policy:</h3>
      <p>Clients may cancel ongoing services or subscriptions by providing 30 days written notice to <a href="mailto:support@marketnest.com">support@marketnest.com</a>. Any applicable terms regarding early termination, outstanding payments, or final deliverables will be governed by the individual service agreement.</p>
      <p>For one-time projects, cancellation terms will be specified in the project agreement. Depending on the stage of project completion at the time of cancellation, a portion of the project fee may still be due.</p>

      <h2>5. Legal or Export Restrictions</h2>
      <p>MarketNest provides digital marketing services globally, subject to applicable international laws and regulations. We do not knowingly engage with businesses or individuals in countries subject to U.S. export restrictions or sanctions.</p>

      <h2>6. Website Privacy Policy</h2>
      <p>Your privacy is important to us. Our Privacy Policy, which details how we collect, use, and protect your personal information, can be found <a href="/privacy-policy">here</a>. (Note: Ensure you have a Privacy Policy page and link it correctly).</p>

      <h2>7. Business Address</h2>
      <p>MarketNest</p>
      <p>2705 Red Gate Ct</p>
      <p>Jamestown, NC 28292</p>
      <p>United States</p>

      <h2>8. Promotions and Offers</h2>
      <p>Any promotions, discounts, or trial offers provided by MarketNest will be subject to specific terms and conditions, which will be clearly communicated at the time of the offer. These terms will outline the duration, eligibility, and any limitations of the promotion.</p>

      <h2>9. Security of Website and Payment Information</h2>
      <p>MarketNest is committed to ensuring the security of your information. Our website uses HTTPS to encrypt data transmitted between your browser and our servers. All payment processing is handled by Stripe, a PCI DSS compliant payment processor. Stripe securely handles your payment information; MarketNest does not store your full credit card details on its servers.</p>
      <p>We adhere to industry best practices to protect our website and your data from unauthorized access and cyber threats.</p>

      <h2>10. Accepted Payment Methods</h2>
      <p>We accept payments through major credit cards. The logos of accepted cards are displayed below:</p>
      <PaymentLogosContainer>
        <PaymentLogo src="/visa.jpg" alt="Visa" />
        <PaymentLogo src="/mastercard.png" alt="Mastercard" />
        <PaymentLogo src="/americanexpress.png" alt="American Express" />
        <PaymentLogo src="/discover.png" alt="Discover" />
      </PaymentLogosContainer>

      <h2>11. Card Network Policies</h2>
      <p>As a merchant, MarketNest complies with the rules and regulations set forth by the card networks (Visa, Mastercard, American Express, Discover). We strive to maintain a transparent and secure transaction environment for all our clients.</p>

      <p><em>This Fulfillment Policy was last updated on June 15, 2025.</em></p>
    </PolicyContainer>
  );
};

export default FulfillmentPolicy;
